import { FC, useRef, useState } from 'react';
import { TextareaAutosize, FormHelperText, Grid } from '@material-ui/core';
import { Formik, Form, FormikState, Field, FormikProps, FieldProps } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { followUpApplication as followUpApplicationApi } from 'http/admin';
import { CustomCheckGroupField } from 'components/inputs';
import { companyDocumentTypeOptions } from 'core/constants';
import { AlertDialog } from 'components/AlertDialog';
import { useAdmin } from 'store/admin/hooks';
import { CompanyDocumentType } from 'core/types';
import moment, { Moment } from 'moment';
import { SendAtDatePicker } from 'components/SendAtDatePicker';
import useStyles from './MultiFollowUpModal.styles';

interface FormValues {
  documentsRequested: CompanyDocumentType[];
  notes: string;
  send_at: Date;
}

interface MultiFollowUpModalProps {
  open: boolean;
  toggleOpen: () => void;
  applicationIds: string[];
}

const MultiFollowUpModal: FC<MultiFollowUpModalProps> = ({ open, toggleOpen, applicationIds }) => {
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<FormValues> | null>(null);
  const { setError, setSuccess } = useAdmin();

  const followUpSchema = yup.object({
    documentsRequested: yup
      .array(yup.string())
      .min(1, t('pages.lead.admin.followUp.inputs.documentsRequested.required')),
    notes: yup.string().nullable(),
    send_at: yup
      .date()
      .required('Required')
      .max(moment().add(72, 'hours').toDate(), t('pages.lead.admin.sendEmail.inputs.send_at.max')),
  });

  const initialValues: FormValues = {
    documentsRequested: [],
    notes: '',
    send_at: moment().toDate(),
  };

  const onSubmit = async (values: FormValues) => {
    if (!applicationIds || applicationIds.length === 0) return;

    setLoading(true);
    try {
      await Promise.all(
        applicationIds.map((id) =>
          followUpApplicationApi(
            id,
            values.documentsRequested,
            values.notes,
            undefined, // no offer_id
            values.send_at,
          ),
        ),
      );

      formRef.current?.resetForm();
      toggleOpen();
      setSuccess(t('pages.lead.admin.followUp.messages.success') as string);
      // Optionally: refresh UI per app ID
    } catch (error) {
      setError((error as Error).message ?? true);
    }
    setLoading(false);
  };

  const onCancel = (resetForm: (nextState?: Partial<FormikState<typeof initialValues>> | undefined) => void) => {
    resetForm();
    toggleOpen();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={followUpSchema} innerRef={formRef}>
      {({ handleChange, values, handleSubmit, touched, errors, resetForm, setFieldValue }) => (
        <AlertDialog
          open={open}
          dialogContentTitle={`${t(`pages.lead.admin.followUp.title.application`)}s`}
          dialogContentText=""
          handleCancel={() => onCancel(resetForm)}
          handleConfirm={handleSubmit}
          confirmButtonTitle={t('pages.lead.admin.followUp.buttons.confirm')}
          loading={loading}
        >
          <Form noValidate>
            <Grid container direction="column" spacing={4}>
              <Grid item>
                <Field
                  id="documentsRequested"
                  name="documentsRequested"
                  component={CustomCheckGroupField}
                  options={companyDocumentTypeOptions}
                  value={values.documentsRequested}
                  onChange={(value: string) => {
                    const alreadyIn = values.documentsRequested.find((info) => info === value);
                    if (alreadyIn) {
                      setFieldValue(
                        'documentsRequested',
                        values.documentsRequested.filter((info) => info !== value),
                      );
                    } else {
                      setFieldValue('documentsRequested', [...values.documentsRequested, value]);
                    }
                  }}
                  title={t('pages.lead.admin.followUp.inputs.documentsRequested.label')}
                  className={classes.documentsRequested}
                  disabled={loading}
                />
              </Grid>

              <Grid item className={classes.notesContainer}>
                <TextareaAutosize
                  id="notes"
                  value={values.notes || ''}
                  aria-label="minimum height"
                  rowsMin={5}
                  onChange={handleChange}
                  className={touched.notes && errors.notes ? classes.textAreaError : ''}
                  placeholder={t('pages.lead.admin.followUp.inputs.notes.placeholder')}
                  disabled={loading}
                />

                {touched.notes && errors.notes && <FormHelperText error>{errors.notes}</FormHelperText>}
              </Grid>

              <Grid item>
                <Field name="send_at" toolbarTitle showToolbar>
                  {({ field, form, meta }: FieldProps) => (
                    <SendAtDatePicker
                      title={t('pages.lead.admin.sendEmail.inputs.send_at.label')}
                      field={field}
                      form={form}
                      meta={meta}
                      onChange={(date: Moment | null) => setFieldValue('send_at', date ?? null)}
                    />
                  )}
                </Field>
              </Grid>
            </Grid>
          </Form>
        </AlertDialog>
      )}
    </Formik>
  );
};

export default MultiFollowUpModal;
